import { Fragment, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../constants/validation'
import { AIButtonContainedLight } from '../../../AIButton'
import { FormikField } from '../../../Formik'
import { ICON_CHEVRON_DIRECTION, IconChevron } from '../../../Icon'
import { TextareaElement } from '../../../Textarea'
import {
  AI_PROMPT_MAX_LENGTH,
  AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT,
  QA_NAME_AI_TEXT_GENERATION_GENERATE_BUTTON,
  QA_NAME_AI_TEXT_GENERATION_INPUT,
} from '../../AITextGeneration.constants'
import {
  AIVoiceInput,
  isAIVoiceInputStateApplying,
  isAIVoiceInputStateConnecting,
  isAIVoiceInputStateDefault,
  isAIVoiceStopActive,
} from '../../AIVoiceInput'
import { AI_TEXT_GENERATION_FORM_FILEDS_TEXTAREA_MIN_ROWS } from '../AITextGenerationFormFields.constants'

import * as styles from './AITextGenerationFormFieldsTopRow.module.css'

import ArrowIcon from '../../../../icons/outline-arrow-left.svg'
import RestoreIcon from '../../../../icons/restore.svg'

function AITextGenerationFormFieldsTopRow({
  disabled,
  generateButtonText,
  hasGeneratedOnce,
  isGenerateDisabled,
  isGenerating,
  isInteractionDisabled,
  isMobile,
  isPromptLimitReached,
  voice: { placeholderKey: voicePlaceholderKey, ...voice } = EMPTY_OBJECT,
}) {
  const { t } = useTranslation()

  const { setFieldValue, submitForm, values } = useFormikContext()

  const promptValue = values[AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT]

  const textareaRef = useRef(null)

  useEffect(() => {
    if (voice.isActive && textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight
    }
  }, [promptValue, voice.isActive])

  function handleGenerateClick() {
    submitForm().then(() => {
      setFieldValue(
        AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT,
        FORMIK_EMPTY_VALUE
      )
    })
  }

  const handleVoiceButtonClick = isAIVoiceStopActive(voice)
    ? voice.stop
    : voice.start

  const isVoiceLimitDisabled =
    isPromptLimitReached && isAIVoiceInputStateDefault(voice.state)

  const isVoiceButtonDisabled =
    disabled ||
    isGenerating ||
    isAIVoiceInputStateConnecting(voice.state) ||
    isAIVoiceInputStateApplying(voice.state) ||
    isVoiceLimitDisabled

  return (
    <div className={styles.topRow}>
      <div className={styles.promptTextareaWrap}>
        <FormikField
          autoResizeHeight
          borderless
          withoutManualResize
          Component={TextareaElement}
          Field={Fragment}
          className={styles.promptTextarea}
          disabled={isInteractionDisabled}
          elementRef={textareaRef}
          maxLength={AI_PROMPT_MAX_LENGTH}
          minRows={AI_TEXT_GENERATION_FORM_FILEDS_TEXTAREA_MIN_ROWS}
          name={AI_TEXT_GENERATION_FORM_FIELD_NAME_PROMPT}
          placeholder={t(
            voicePlaceholderKey || 'AITextGeneration.InputPlaceholderKey'
          )}
          qaName={QA_NAME_AI_TEXT_GENERATION_INPUT}
        />
      </div>
      <AIVoiceInput
        disabled={isVoiceButtonDisabled}
        state={voice.state}
        tooltip={
          isPromptLimitReached
            ? t('AITextGeneration.VoicePromptLimitReachedKey')
            : null
        }
        onClick={handleVoiceButtonClick}
      />
      <AIButtonContainedLight
        beforeIconProps={
          hasGeneratedOnce
            ? { Icon: RestoreIcon }
            : {
                Icon: IconChevron,
                iconProps: {
                  Icon: ArrowIcon,
                  direction: ICON_CHEVRON_DIRECTION.UP,
                },
              }
        }
        disabled={isGenerateDisabled}
        isLoading={isGenerating}
        qaName={QA_NAME_AI_TEXT_GENERATION_GENERATE_BUTTON}
        text={isMobile ? null : generateButtonText}
        onClick={handleGenerateClick}
      />
    </div>
  )
}

export default AITextGenerationFormFieldsTopRow
